import React from "react";
import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid2,
  useTheme,
  Skeleton,
  Avatar,
} from "@mui/material";
import { useGetScheduleProgressKpiQuery } from "store/slices/apis/scheduleDashboarSlice";
import { useParams } from "react-router-dom";
import moment from "moment";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import ProjectStatusOverviewHead from "components/ProjectStatusOverviewHead";
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"
import { MdOutlineArrowOutward } from "react-icons/md";

const ProgressKPI = () => {
  const [kpiData, setKpiData] = useState([]);
  // Grid item layout: 4 columns on desktop (md=3)
  const gridItemProps = { xs: 12, sm: 6, md: 3 };
  const theme = useTheme();
  const { projectId } = useParams();
  const { data, isLoading } = useGetScheduleProgressKpiQuery({ projectId });

  const lastUpdated = data?.lastUpdated;

  useEffect(() => {
    if (data) {
      setKpiData([data]);
    }
  }, [data]);



  if (isLoading) {
    return (
      <>
        <Grid2 container spacing={5} sx={{ mt: "20px" }}>
          <ProjectStatusOverviewHead
            lastModified={lastUpdated}
            marginBottm={"10px"}
          />
        </Grid2>
        <Grid2 container spacing={5} sx={{ mt: "20px" }}>
          {Array(4)
            .fill({})
            .map((_, index) => (
              <Grid2 key={index} size={{ xs: 12, md: 3 }}>
                <Card
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #25252517",
                    borderRadius: "3px",
                  }}
                >
                  <Box display="flex" alignItems="center" p={2}>
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ mr: 2, borderRadius: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="text" width="30%" height={20} />
                      </Box>
                      <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                  </Box>
                </Card>
              </Grid2>
            ))}
        </Grid2>

        <Grid2 container spacing={5} sx={{ mt: "10px" }}>
          {Array(2)
            .fill({})
            .map((_, index) => (
              <Grid2 key={index} size={{ xs: 12, md: 3 }}>
                <Card
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #25252517",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" p={2}>
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ mr: 2, borderRadius: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="text" width="30%" height={20} />
                      </Box>
                      <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                  </Box>
                </Card>
              </Grid2>
            ))}
        </Grid2>
      </>
    );
  }

  const cardsData = [
    {
      value: isLoading ? "--" : data?.totalCriticalActivities ?? 0,
      change: isLoading ? "" : data?.totalCriticalActivitiesChange ?? 0,
      timeFrame: "this month",
      label: "Total Critical Activities",
      color: "#9ED3CC",
      changeColor:
        (data?.totalCriticalActivitiesChange ?? 0) < 0 ? "#CE412D" : "#1E8E3E",
      hasChange: true,
    },
    {
      value: isLoading ? "--" : data?.delayInDays ?? 0,
      change: isLoading ? "" : data?.delayInDaysChange ?? 0,
      timeFrame: "this month",
      label: "Delay in Days",
      color: "#9ED3CC",
      changeColor: (data?.delayInDaysChange ?? 0) < 0 ? "#CE412D" : "#1E8E3E",
      hasChange: false,
    },
    {
      value: isLoading ? "--" : data?.criticalPathVariance ?? 0,
      change: isLoading ? "" : data?.criticalPathVarianceChange ?? 0,
      timeFrame: "this month",
      label: "Critical Path Variance",
      color: "#9ED3CC",
      changeColor:
        (data?.criticalPathVarianceChange ?? 0) < 0 ? "#CE412D" : "#1E8E3E",
      hasChange: false,
    },
    {
      value: isLoading ? "--" : `${data?.scheduleVariancePercent ?? 0}%`,
      change: isLoading ? "" : data?.scheduleVarianceChange ?? 10,
      timeFrame: "this month",
      label: "Schedule Variance",
      color: "#9ED3CC",
      changeColor:
        (data?.scheduleVarianceChange ?? 0) < 0 ? "#CE412D" : "#1E8E3E",
      hasChange: false,
    },
  ];

  const card = [
    {
      value: data?.forecastCompletionDate
        ? moment(data?.forecastCompletionDate).format("D MMMM, YYYY")
        : "--",
      isDate: true,
      label: "Forecast Completion Date",
      color: "#9ED3CC",
      changeColor: "#D32F2F",
    },
    {
      value: data?.plannedCompletionDate
        ? moment(data?.plannedCompletionDate).format("D MMMM, YYYY")
        : "--",
      isDate: true,
      label: "Planned Completion Date",
      color: "#9ED3CC",
      changeColo: "#CDFBE7",
    },
  ];

  const cardsDataKpi = cardsData?.map((item) => {
    const changeValue = item.change;
    const arrow = changeValue >= 0 ? "up" : "down";
    const isPositive = changeValue > 0 ? true : false;
    const absoluteChange = isNaN(changeValue)
      ? "-"
      : changeValue === -100
        ? "0%"
        : `${Math.abs(changeValue)}%`;
    const redColor = "#CE412D";
    return {
      ...item,
      arrow,
      absoluteChange,
      isPositive,
      redColor,
    };
  });



  return (
    <>
      <Grid2 container spacing={5} sx={{ mt: "20px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"20px"}
        />
      </Grid2>

      <Grid2 container spacing={2} sx={{ mt: "10px", flexWrap: "wrap", }}>
        {cardsDataKpi?.map((card, index) => (
          <Grid2 key={index} size={{ xs: 12, md: 3 }}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    sx={{ bgcolor: card.color, width: 45, height: 45, mr: 2 }}
                  />
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: "18px",
                          mr: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {card.value}
                      </Typography>
                      <Chip
                        label={card.absoluteChange}
                        size="small"
                        sx={{
                          backgroundColor: "#E6F4EA",
                          color: card?.isPositive
                            ? card?.redColor
                            : card.changeColor,
                          fontWeight: 600,
                          fontSize: "12px",
                          borderRadius: "4px",
                          height: "20px",
                        }}
                        icon={
                          card?.absoluteChange !== "0%" &&
                          // (card.arrow === "up" ? (
                          //   <ArrowUpward
                          //     sx={{
                          //       fontSize: "12px !important",
                          //       color: !card.isPositive
                          //         ? `${card.changeColor} !important`
                          //         : `${card?.redColor} !important`,
                          //     }}
                          //   />
                          // ) : (
                          //   <ArrowDownward
                          //     sx={{
                          //       fontSize: "12px !important",
                          //       color: !card.isPositive
                          //         ? `${card.changeColor} !important`
                          //         : `${card.redColor} !important`,
                          //     }}
                          //   />
                          // ))
                          (card.arrow === "up" ? (
                            <ArrowUpIcon
                              style={{
                                height: "12px",
                                width: "12px",
                                fill: !card.isPositive ? card.changeColor : card.redColor,
                              }}
                            />
                          ) : (
                            <ArrowDownIcon
                              style={{
                                height: "12px",
                                width: "12px",
                                fill: !card.isPositive ? card.changeColor : card.redColor,
                              }}
                            />
                          ))

                        }
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1, whiteSpace: "nowrap" }}
                      >
                        {card.timeFrame}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {card.label}
                    </Typography>
                  </Box>
                </Box>
                {card.hasChange && (
                  <Avatar
                    sx={{
                      bgcolor: "#fff",
                      width: "37px",
                      height: "37px",
                      border: "1px solid #d3d3d3",
                    }}
                  >
                    <MdOutlineArrowOutward
                      style={{
                        color: "#252525",
                        fontSize: "20px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const el = document.getElementById("schedule-variance-drivers");
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                    />
                  </Avatar>
                )}
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>

      <Grid2 container spacing={2} sx={{ mt: "15px", flexWrap: "wrap", }}>
        {card.map((cardItem, index) => (
          <Grid2 key={index} size={{ xs: 12, md: 3 }}>
            <Card
              sx={{
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                borderRadius: "3px",
              }}
            >
              <Box display="flex" alignItems="center" p={2}>
                <Typography
                  sx={{ bgcolor: cardItem.color, width: 45, height: 45, mr: 2 }}
                />
                <Box>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mr: 1,
                        color: cardItem.changeColor,
                        fontSize: "18px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cardItem.value}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 500,
                      fontSize: "13px",
                      lineHeight: "100%",
                      letterSpacing: "-0.4px",
                      color: "#252525",
                      whiteSpace: "nowrap",
                      opacity: 0.61,
                    }}
                  >
                    {cardItem.label}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    </>
  );
};

export default ProgressKPI;